import { useMemo, useState } from 'react';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, maxBy } from 'es-toolkit';

import EmptyRecords from '@/views/components/Common/EmptyRecords';
import CommonModal from '@/views/components/Modals/CommonModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import Paper from '@/views/components/Paper/Paper';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';
import FooterContentCard from '@/views/screens/common/FooterContentCard';

import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import AddProductFullModal from '../../../common/AddProductFull/AddProductFullModal';
import LADEmailStatus from '../../EmailStatus/LADEmailStatus';
import { useGetClaimDetails, useGetClaimItems, useSaveClaim } from '../../hooks';
import { getTabDisplayState } from '../utils/getTabDisplayState';
import LADClaimsItemCard from './LADClaimsItemCard';

const LADClaimsItemsTab = ({ claimKey }) => {
  const [addProductModal, setAddProductModal] = useState(false);
  const [addProductModalIndex, setAddProductModalIndex] = useState(false);
  const [newItems, setNewItems] = useState([]);
  const [itemEditModes, setItemEditModes] = useState({});
  const { data: claimDetails } = useGetClaimDetails(claimKey);
  const {
    data: claimItemsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useGetClaimItems({ claimKey });
  const { mutate: saveClaim } = useSaveClaim();
  const [openEmailStatus, setOpenEmailStatus] = useState(false);

  const claimItems = useMemo(() => {
    if (!claimItemsData) {
      return { data: [], totalProductClaimAmount: 0, totalRows: 0 };
    }
    return {
      data: claimItemsData.items ?? [],
      totalProductClaimAmount: claimItemsData.totalProductClaimAmount || 0,
      totalRows: claimItemsData.totalRows || 0,
    };
  }, [claimItemsData]);
  const itemList = useMemo(() => {
    const uniqueExistingItems = Array.from(
      new Map((claimItems.data || []).map((data) => [data.itemSeq, data])).values(),
    );
    return [
      ...uniqueExistingItems.map((data) => ({
        ...data,
        editMode: itemEditModes[data.itemSeq] ?? data.editMode ?? false,
        isNew: false,
      })),
      ...newItems,
    ];
  }, [claimItems.data, itemEditModes, newItems]);
  const { showContent, showEmpty, showFullLoader, showNextPageLoader } = useMemo(
    () =>
      getTabDisplayState({
        hasRows: itemList.length > 0,
        isFetching,
        isFetchingNextPage,
        isLoading,
      }),
    [isFetching, isFetchingNextPage, isLoading, itemList.length],
  );

  const setEditMode = (index, val) => {
    const item = itemList[index];
    if (!item) {
      return;
    }
    if (item.isNew) {
      setNewItems((prev) => prev.map((row) => (row.itemSeq === item.itemSeq ? { ...row, editMode: val } : row)));
      return;
    }
    setItemEditModes((prev) => ({ ...prev, [item.itemSeq]: val }));
  };

  const handleCancel = (index, isNew) => {
    const item = itemList[index];
    if (!item) {
      return;
    }
    if (isNew) {
      setNewItems((prev) => prev.filter((row) => row.itemSeq !== item.itemSeq));
    } else {
      setItemEditModes((prev) => ({ ...prev, [item.itemSeq]: false }));
    }
  };

  const handleSubmit = (data) => {
    if (!claimDetails) {
      return;
    }
    const itemSeqKey = Number(data.itemSeq);
    const wasNewDraft = newItems.some((row) => Number(row.itemSeq) === itemSeqKey);
    let payload = cloneDeep(claimDetails);
    payload.claimItem = [{ ...data, dirtyFlag: wasNewDraft ? 'I' : 'U' }];
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.section = 'ITEMS';
    payload.pageIndex = claimItemsData?.pageIndex ?? 1;
    saveClaim(payload, {
      onSuccess: () => {
        setItemEditModes((prev) => ({ ...prev, [itemSeqKey]: false }));
        if (wasNewDraft) {
          setNewItems((prev) => prev.filter((row) => Number(row.itemSeq) !== itemSeqKey));
        }
      },
    });
  };

  const handleDelete = (index) => {
    const selectedItem = itemList[index];
    if (!selectedItem) {
      return;
    }
    if (selectedItem.isNew) {
      setNewItems((prev) => prev.filter((row) => row.itemSeq !== selectedItem.itemSeq));
      return;
    }
    if (!claimDetails) {
      return;
    }
    if (claimDetails?.claimItem?.length === 1) {
      showNotification({
        message: 'At least one claim item is required. Cannot delete.',
        type: NOTIFICATION_TYPES.ERROR,
      });
    } else {
      let payload = cloneDeep(claimDetails);
      payload.claimItem = [{ ...selectedItem, dirtyFlag: 'D' }];
      payload = LADCovertDatesFromMomentToAPIType(payload);
      payload.section = 'ITEMS';
      payload.pageIndex = claimItemsData?.pageIndex ?? 1;
      saveClaim(payload);
    }
  };

  const handleAddNew = () => {
    const allItems = [...claimItems.data, ...newItems];
    let nextSequence = maxBy(allItems, ({ itemSeq }) => Number(itemSeq));
    nextSequence = nextSequence ? nextSequence.itemSeq : 0;
    const newItem = {
      claimKey: claimDetails?.claimKey,
      dirtyFlag: 'i',
      dispositionMethod: '',
      editMode: true,
      fullRelFlag: '',
      isNew: true,
      itemCode: '',
      itemDesc: '',
      itemQuantity: undefined,
      itemSeq: nextSequence + 1,
      itemUnitMeasure: '',
      measure: '',
      smAmount: undefined,
      smFlag: '',
      smType: '',
      value: undefined,
    };
    setNewItems((prev) => [...prev, newItem]);
  };

  const handleNewProductModal = (index) => {
    setAddProductModalIndex(index);
    setAddProductModal(true);
  };

  const onSelectAddressBook = (data) => {
    const selectedItem = itemList[addProductModalIndex];
    if (!selectedItem?.isNew) {
      handleAddProductModalClose();
      return;
    }
    setNewItems((prev) =>
      prev.map((row) =>
        row.itemSeq === selectedItem.itemSeq
          ? {
              ...row,
              claimantValue: data.claimantValue,
              editMode: true,
              fullRelFlag: data.fullRelFlag,
              isNew: true,
              itemCode: data.productCode,
              itemDesc: data.productDesc,
              itemUnitMeasure: data.productUnitMeasure,
              itemUnitWeight: data.productWeight,
            }
          : row,
      ),
    );
    handleAddProductModalClose();
  };

  const handleAddProductModalClose = () => {
    setAddProductModal(false);
    setAddProductModalIndex(false);
  };

  const handleNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleOpenEmailStatus = () => {
    setOpenEmailStatus(false);
  };

  const handleOpenEmailModel = () => {
    setOpenEmailStatus(true);
  };
  return (
    <div className="d-flex flex-direction-column h-100 overflow-hidden pb-16">
      <Row className="align-items-center pt-15 px-30" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Claim Items</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={() => handleOpenEmailModel()}>View Email Status</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100 " />
      {showFullLoader ? (
        <div className="px-30 py-10 flex-1" data-testid="lad-items-loading">
          <ContentSpinner />
        </div>
      ) : showContent ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={showNextPageLoader}
          hasMore={isFetchingNextPage ? false : hasNextPage}
          scrollOffset={30}
        >
          {itemList.map((row, index) => (
            <LADClaimsItemCard
              itemDetails={row}
              key={row.itemSeq}
              index={index}
              editMode={Boolean(row.editMode)}
              setEditMode={setEditMode}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
              handleNewProductModal={handleNewProductModal}
              handleAddNew={handleAddNew}
              totalItemsCount={itemList.length}
            />
          ))}
        </ScrollPagination>
      ) : showEmpty ? (
        <EmptyRecords />
      ) : null}
      <div className="px-30">
        <Paper className="background-header px-16 py-5">
          <Row justify="space-around" className="px-10" gutter={10}>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={claimItems.totalProductClaimAmount}
                label="Total Product Claim Amount"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
                defaultValue={0}
              />
            </Col>
          </Row>
        </Paper>
      </div>
      <AddProductFullModal
        addressBookModal={addProductModal && addProductModalIndex !== false}
        onSelectAddressBook={onSelectAddressBook}
        handleAddressBookModalClose={handleAddProductModalClose}
      />
      <CommonModal
        title="Email Tracking Status"
        open={openEmailStatus}
        className="vh-80 vw-90"
        onClose={handleOpenEmailStatus}
        destroyOnHidden
        // biome-ignore lint/complexity/noUselessFragments: placeholder
        customFooter={<></>}
      >
        <LADEmailStatus claimKey={claimDetails?.claimKey} />
      </CommonModal>
    </div>
  );
};

export default LADClaimsItemsTab;
